import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FiPlus, FiTrash2, FiRefreshCw, FiSend, FiList, FiBarChart } from 'react-icons/fi';
import api from '../services/api';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import CreateNotificationModal from '../components/admin/CreateNotificationModal';
import NotificationsList from '../components/admin/NotificationsList';

const NotificationsAdminPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('list'); // 'global', 'list', ou 'stats'

  // eslint-disable-next-line no-unused-vars
  const queryClient = useQueryClient();

  // Récupérer les statistiques des notifications
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useQuery(
    'notifications-stats',
    () => api.get('/notifications/stats').then(res => res.data.data),
    {
      enabled: activeTab === 'stats'
    }
  );

  // Mutation pour nettoyer les notifications expirées
  const cleanupMutation = useMutation(
    () => api.post('/notifications/cleanup'),
    {
      onSuccess: (response) => {
        toast.success(`${response.data.message}`);
        refetchStats();
      },
      onError: (error) => {
        toast.error('Erreur lors du nettoyage');
      }
    }
  );

  const handleCleanup = async () => {
    const result = await Swal.fire({
      title: 'Nettoyer les notifications expirées',
      text: 'Cette action va supprimer définitivement toutes les notifications expirées. Cette opération est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Oui, nettoyer',
      cancelButtonText: 'Annuler',
      customClass: {
        popup: 'swal2-popup-custom'
      }
    });

    if (result.isConfirmed) {
      try {
        await cleanupMutation.mutateAsync();
        Swal.fire({
          title: 'Nettoyage terminé !',
          text: 'Toutes les notifications expirées ont été supprimées avec succès.',
          icon: 'success',
          confirmButtonColor: '#059669',
          timer: 3000,
          timerProgressBar: true
        });
      } catch (error) {
        Swal.fire({
          title: 'Erreur',
          text: 'Une erreur est survenue lors du nettoyage.',
          icon: 'error',
          confirmButtonColor: '#dc2626'
        });
      }
    }
  };

  const handleRefreshStats = async () => {
    try {
      await refetchStats();
      Swal.fire({
        title: 'Statistiques actualisées',
        text: 'Les données ont été mises à jour avec succès.',
        icon: 'success',
        confirmButtonColor: '#059669',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({
        title: 'Erreur',
        text: 'Impossible de récupérer les statistiques.',
        icon: 'error',
        confirmButtonColor: '#dc2626'
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Administration des Notifications</h1>
          <p className="text-gray-600 mt-1">
            Gérez les notifications globales et consultez les statistiques
          </p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <FiPlus className="h-4 w-4 mr-2" />
          Nouvelle notification globale
        </button>
      </div>

      {/* Onglets */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('global')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'global'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiPlus className="inline h-4 w-4 mr-1" />
            Créer une notification
          </button>
          <button
            onClick={() => setActiveTab('list')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'list'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiList className="inline h-4 w-4 mr-1" />
            Liste des notifications
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'stats'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiBarChart className="inline h-4 w-4 mr-1" />
            Statistiques
          </button>
        </nav>
      </div>

      {/* Contenu selon l'onglet */}
      {activeTab === 'global' && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="text-center py-12">
            <FiSend className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              Créer une notification globale
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Envoyez une notification à tous les utilisateurs ou à un rôle spécifique
            </p>
            <div className="mt-6">
              <button
                onClick={() => setIsModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FiPlus className="h-4 w-4 mr-2" />
                Créer une notification
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'list' && (
        <NotificationsList />
      )}

      {activeTab === 'stats' && (
        <div className="space-y-6">
          {/* Actions d'administration */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Actions d'administration
            </h3>
            <div className="flex space-x-4">
              <button
                onClick={handleRefreshStats}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FiRefreshCw className="h-4 w-4 mr-2" />
                Actualiser
              </button>
              <button
                onClick={handleCleanup}
                disabled={cleanupMutation.isLoading}
                className="inline-flex items-center px-4 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                <FiTrash2 className="h-4 w-4 mr-2" />
                {cleanupMutation.isLoading ? 'Nettoyage...' : 'Nettoyer expirées'}
              </button>
            </div>
          </div>

          {/* Statistiques */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Statistiques des notifications
            </h3>

            {statsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : stats ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{stats.total_notifications || 0}</div>
                  <div className="text-sm text-blue-800">Total notifications</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{stats.active_notifications || 0}</div>
                  <div className="text-sm text-green-800">Notifications actives</div>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">{stats.unread_notifications || 0}</div>
                  <div className="text-sm text-yellow-800">Non lues</div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{stats.expired_notifications || 0}</div>
                  <div className="text-sm text-red-800">Expirées</div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FiRefreshCw className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p>Cliquez sur "Actualiser" pour charger les statistiques</p>
              </div>
            )}
          </div>

          {/* Informations sur les types de notifications */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Types de notifications disponibles
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <span className="text-xl">ℹ️</span>
                <div>
                  <div className="font-medium">Information</div>
                  <div className="text-sm text-gray-500">Messages informatifs généraux</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-xl">✅</span>
                <div>
                  <div className="font-medium">Succès</div>
                  <div className="text-sm text-gray-500">Confirmation d'actions réussies</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-xl">⚠️</span>
                <div>
                  <div className="font-medium">Avertissement</div>
                  <div className="text-sm text-gray-500">Alertes importantes</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-xl">❌</span>
                <div>
                  <div className="font-medium">Erreur</div>
                  <div className="text-sm text-gray-500">Messages d'erreur critiques</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de création */}
      <CreateNotificationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default NotificationsAdminPage;
